import type { ChatClient, ChatMessage, ChatRead, Moderator } from './client';
import type { Copy } from './i18n';

/**
 * The chat panel: messages, the pinned one, the composer and the state line.
 * Every text is set with textContent; nothing a viewer typed ever becomes
 * HTML. Only the owner's posts (role host or moderator) get clickable links.
 */
export interface PanelOptions {
  /** Show a close button (the embedded player's overlay). */
  closable?: boolean;
  onClose?: () => void;
  /** Whether the stream is still waiting to start (for the "closed" wording). */
  isPending?: () => boolean;
}

const MAX_SHOWN = 200;
const STICK_PX = 48;
const ANNOUNCE_EVERY_MS = 5000;

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (text !== undefined) el.textContent = text;
  return el;
};

/** A stable colour per nickname, from the panel's palette. */
function hueOf(name: string): number {
  let x = 0;
  for (const c of name.toLowerCase()) x = (x * 31 + c.codePointAt(0)!) >>> 0;
  return [200, 160, 280, 330, 40, 110, 20, 250][x % 8];
}

/** The owner's text with http(s) links made clickable; everyone else's stays plain. */
function fillText(el: HTMLElement, m: ChatMessage) {
  if (!m.role) {
    el.textContent = m.text;
    return;
  }
  for (const part of m.text.split(/(https?:\/\/[^\s<>"]+)/g)) {
    if (/^https?:\/\//.test(part)) {
      let href: string | null = null;
      try {
        const u = new URL(part);
        if (u.protocol === 'http:' || u.protocol === 'https:') href = u.toString();
      } catch {
        href = null;
      }
      if (href) {
        const a = h('a', 'jc-link', part);
        a.href = href;
        a.target = '_blank';
        a.rel = 'noopener noreferrer nofollow ugc';
        el.append(a);
        continue;
      }
    }
    el.append(document.createTextNode(part));
  }
}

export function mountPanel(host: HTMLElement, client: ChatClient, t: Copy, opts: PanelOptions = {}) {
  const root = h('section', 'jc');
  root.setAttribute('aria-label', t.title);

  const head = h('header', 'jc-head');
  head.append(h('span', 'jc-dot'), h('h2', 'jc-title', t.title));
  const close = h('button', 'jc-close');
  close.type = 'button';
  close.setAttribute('aria-label', t.close);
  close.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  close.hidden = !opts.closable;
  close.addEventListener('click', () => opts.onClose?.());
  head.append(close);

  // Shown only to a moderator (a moderator link was opened in this browser).
  const modbar = h('div', 'jc-modbar');
  modbar.hidden = true;
  const modName = h('span', 'jc-modname');
  const slowSel = h('select', 'jc-slow');
  slowSel.setAttribute('aria-label', t.slow);
  for (const v of [0, 5, 10, 30, 60, 120]) {
    const o = h('option', '', v ? `${t.slow} ${v} s` : `${t.slow}: ${t.off}`);
    o.value = String(v);
    slowSel.append(o);
  }
  const pauseBtn = h('button', 'jc-modbtn', t.pause);
  pauseBtn.type = 'button';
  modbar.append(modName, slowSel, pauseBtn);

  const pin = h('div', 'jc-pin');
  pin.hidden = true;

  const scroll = h('div', 'jc-scroll');
  const list = h('ol', 'jc-list');
  list.setAttribute('role', 'log');
  list.setAttribute('aria-live', 'off');
  const empty = h('p', 'jc-empty', t.empty);
  scroll.append(list, empty);

  const newer = h('button', 'jc-newer', `${t.newMessages} ↓`);
  newer.type = 'button';
  newer.hidden = true;

  const reactSlot = h('div', 'jc-react-slot');
  const status = h('div', 'jc-status');
  status.setAttribute('role', 'status');

  const form = h('form', 'jc-form');
  form.noValidate = true;
  const nickRow = h('div', 'jc-nick');
  const nickLabel = h('label', 'jc-nick-label', t.nickPrompt);
  const nick = h('input', 'jc-field');
  nick.id = `jc-nick-${Math.random().toString(36).slice(2, 8)}`;
  nickLabel.htmlFor = nick.id;
  nick.maxLength = 24;
  nick.setAttribute('autocomplete', 'nickname');
  nick.placeholder = t.nickPlaceholder;
  nickRow.append(nickLabel, nick);
  const row = h('div', 'jc-row');
  const input = h('input', 'jc-field jc-input');
  input.maxLength = 200;
  input.placeholder = t.placeholder;
  input.enterKeyHint = 'send';
  input.setAttribute('aria-label', t.placeholder);
  const send = h('button', 'jc-send', t.send);
  send.type = 'submit';
  row.append(input, send);
  const meta = h('div', 'jc-meta');
  const as = h('span', 'jc-as');
  const count = h('span', 'jc-count');
  meta.append(as, count);
  form.append(nickRow, row, meta);

  const announcer = h('div', 'jc-sr');
  announcer.setAttribute('aria-live', 'polite');

  root.append(head, modbar, pin, scroll, newer, reactSlot, status, form, announcer);
  host.append(root);

  // ---------------------------------------------------------------- messages
  const shown = new Map<string, { m: ChatMessage; el: HTMLLIElement }>();
  let pendingSeq = 0;
  let unannounced = 0;
  let lastAnnounce = 0;
  let announceTimer = 0;

  function render(m: ChatMessage, pending = false): HTMLLIElement {
    const li = h('li', 'jc-msg');
    li.dataset.id = m.id;
    if (pending) li.classList.add('is-pending');
    if (m.role) {
      li.classList.add(`is-${m.role}`);
      const badge = h('span', `jc-badge jc-badge-${m.role}`, m.role === 'host' ? t.host : t.moderator);
      li.append(badge);
    }
    const name = h('b', 'jc-name', m.nickname);
    if (!m.role) name.style.setProperty('--h', String(hueOf(m.nickname)));
    const text = h('span', 'jc-text');
    fillText(text, m);
    li.append(name, text);
    if (!pending) {
      // Moderator actions: in every message, shown only while root has .is-mod.
      const acts = h('span', 'jc-acts');
      for (const [act, label] of [['pin', t.pin], ['del', t.del], ...(m.role ? [] : [['ban', t.ban]])] as const) {
        const b = h('button', 'jc-act', label);
        b.type = 'button';
        b.dataset.act = act;
        acts.append(b);
      }
      li.append(acts);
    }
    li.title = new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return li;
  }

  const nearBottom = () => scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < STICK_PX;
  const toBottom = () => {
    scroll.scrollTop = scroll.scrollHeight;
    newer.hidden = true;
  };
  scroll.addEventListener('scroll', () => {
    if (nearBottom()) newer.hidden = true;
  });
  newer.addEventListener('click', toBottom);

  function insert(m: ChatMessage, pending = false) {
    const el = render(m, pending);
    // Keep time order: walk back from the end (new messages land there).
    const at = Date.parse(m.created_at);
    let before: Element | null = null;
    for (let n = list.lastElementChild; n; n = n.previousElementSibling) {
      const e = shown.get((n as HTMLElement).dataset.id!);
      if (!e || Date.parse(e.m.created_at) <= at) break;
      before = n;
    }
    list.insertBefore(el, before);
    shown.set(m.id, { m, el });
  }

  function remove(id: string) {
    const e = shown.get(id);
    if (!e) return;
    e.el.remove();
    shown.delete(id);
  }

  function announce(n: number, single?: ChatMessage) {
    unannounced += n;
    clearTimeout(announceTimer);
    const say = () => {
      if (!unannounced) return;
      announcer.textContent = unannounced === 1 && single ? `${single.nickname}: ${single.text}` : t.announce(unannounced);
      unannounced = 0;
      lastAnnounce = Date.now();
    };
    // At most one announcement every few seconds, however fast chat moves.
    const wait = Math.max(0, lastAnnounce + ANNOUNCE_EVERY_MS - Date.now());
    announceTimer = window.setTimeout(say, wait);
  }

  // ---------------------------------------------------------------- state
  let state: ChatRead['state'] = 'off';
  let moderator: Moderator | null = null;
  let pinnedId: string | null = null;
  let slow = 0;
  let cooldownUntil = 0;
  let cooldownTimer = 0;
  let flash: { text: string; until: number } | null = null;

  function setStatus() {
    let text = '';
    if (flash && Date.now() < flash.until) text = flash.text;
    else if (state === 'paused') text = t.paused;
    else if (state === 'read_only') text = t.readOnly;
    else if (state === 'closed') text = opts.isPending?.() ? t.closedPending : t.closed;
    else if (state === 'off') text = t.closed;
    else if (state === 'open' && slow > 0) text = client.last?.slow_mode_auto ? t.slowModeAuto(slow) : t.slowMode(slow);
    status.textContent = text;
    status.classList.toggle('is-flash', !!flash && Date.now() < flash.until);
    root.dataset.state = state;
    // Moderators can post while chat is paused, like the owner.
    const canPost = state === 'open' || (!!moderator && state === 'paused');
    form.hidden = !canPost;
    const left = Math.ceil((cooldownUntil - Date.now()) / 1000);
    send.disabled = !canPost || left > 0;
    send.textContent = left > 0 ? t.wait(left) : t.send;
  }

  function showFlash(text: string, ms = 5000) {
    flash = { text, until: Date.now() + ms };
    setStatus();
    window.setTimeout(setStatus, ms + 50);
  }

  function cooldown(seconds: number) {
    cooldownUntil = Date.now() + seconds * 1000;
    clearInterval(cooldownTimer);
    cooldownTimer = window.setInterval(() => {
      setStatus();
      if (Date.now() >= cooldownUntil) clearInterval(cooldownTimer);
    }, 250);
    setStatus();
  }

  function setPinned(m: ChatMessage | null) {
    pinnedId = m?.id ?? null;
    pin.hidden = !m;
    pin.replaceChildren();
    if (!m) return;
    const label = h('span', 'jc-pin-label', t.pinned);
    const body = h('div', 'jc-pin-body');
    const name = h('b', 'jc-name', m.nickname);
    const text = h('span', 'jc-text');
    fillText(text, m);
    body.append(name, text);
    pin.append(label, body);
  }

  const off = client.onRead((r) => {
    state = r.state;
    slow = r.slow_mode_seconds;
    const stick = nearBottom();
    if (r.cleared_at) {
      const c = Date.parse(r.cleared_at);
      for (const [id, e] of shown) if (Date.parse(e.m.created_at) <= c && !e.el.classList.contains('is-pending')) remove(id);
    }
    for (const id of r.deleted_ids) remove(id);
    let added = 0;
    let lastAdded: ChatMessage | undefined;
    for (const m of r.messages) {
      if (shown.has(m.id)) continue;
      insert(m);
      added++;
      lastAdded = m;
    }
    while (shown.size > MAX_SHOWN) remove(shown.keys().next().value!);
    empty.hidden = shown.size > 0 || state === 'closed' || state === 'off';
    setPinned(r.pinned);
    if (moderator) {
      pauseBtn.textContent = state === 'paused' ? t.resume : t.pause;
      if (document.activeElement !== slowSel && !r.slow_mode_auto) slowSel.value = String(r.slow_mode_seconds);
    }
    setStatus();
    if (added) {
      if (stick) toBottom();
      else newer.hidden = false;
      announce(added, lastAdded);
    }
  });

  const offOffline = client.onOffline((o) => (o ? showFlash(t.offline, 60_000) : ((flash = null), setStatus())));

  // ---------------------------------------------------------------- composer
  function showNick() {
    if (moderator) {
      nickRow.hidden = true;
      as.replaceChildren(document.createTextNode(`${t.moderating} `), h('b', '', moderator.name));
      return;
    }
    const has = !!client.nickname;
    nickRow.hidden = has;
    as.replaceChildren();
    if (has) {
      as.append(document.createTextNode(`${t.chattingAs} `), h('b', '', client.nickname), document.createTextNode(' · '));
      const ch = h('button', 'jc-linkbtn', t.change);
      ch.type = 'button';
      ch.addEventListener('click', () => {
        nick.value = client.nickname;
        nickRow.hidden = false;
        nick.focus();
        nick.select();
      });
      as.append(ch);
    }
  }
  showNick();

  input.addEventListener('input', () => {
    const n = [...input.value].length;
    count.textContent = n > 160 ? `${n}/200` : '';
  });
  input.addEventListener('focus', () => {
    // Phones: keep the field above the keyboard.
    window.setTimeout(() => input.scrollIntoView({ block: 'nearest' }), 300);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (moderator) {
      if (!text || send.disabled) return;
      input.value = '';
      count.textContent = '';
      const res = await client.mod('POST', '/messages', { text });
      if (res.ok) {
        const m = res.body as ChatMessage;
        if (!shown.has(m.id)) insert(m);
        empty.hidden = true;
        toBottom();
      } else {
        input.value = text;
        showFlash(res.message || t.modFailed);
      }
      return;
    }
    const wanted = (nickRow.hidden ? client.nickname : nick.value).trim();
    if (!wanted || [...wanted].length < 2) {
      nickRow.hidden = false;
      nick.focus();
      if (wanted) showFlash(t.nickPrompt);
      return;
    }
    if (!text || send.disabled) return;
    client.nickname = wanted;
    showNick();
    input.value = '';
    count.textContent = '';
    const temp: ChatMessage = { id: `tmp_${++pendingSeq}`, nickname: wanted, text, role: null, created_at: new Date().toISOString() };
    insert(temp, true);
    empty.hidden = true;
    toBottom();
    cooldown(Math.max(3, slow));
    const res = await client.post(text, wanted);
    remove(temp.id);
    if (res.ok) {
      // Shown at once and kept, even before (or, for a hidden one, instead of) the read.
      if (!shown.has(res.message.id)) insert(res.message);
      toBottom();
      return;
    }
    input.value = input.value || text;
    if (res.code === 'chat_slow_mode' || res.code === 'chat_busy' || res.code === 'chat_rate_limited') {
      cooldown(res.retryAfter ?? 5);
      showFlash(res.code === 'chat_busy' ? t.busy : res.code === 'chat_rate_limited' ? t.tooMany : t.wait(res.retryAfter ?? 5));
    } else {
      cooldownUntil = 0;
      if (res.code === 'chat_message_not_allowed') showFlash(t.noLinks);
      else if (res.code === 'invalid_nickname') {
        nickRow.hidden = false;
        nick.focus();
        showFlash(res.message || t.nickPrompt);
      } else if (res.code === 'chat_paused' || res.code === 'chat_closed') client.refreshSoon();
      else showFlash(res.message || t.failed);
    }
    setStatus();
  });

  // ---------------------------------------------------------------- moderating
  list.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('.jc-act');
    if (!b || !moderator) return;
    const id = b.closest<HTMLElement>('.jc-msg')?.dataset.id;
    const entry = id ? shown.get(id) : undefined;
    if (!id || !entry) return;
    let res;
    if (b.dataset.act === 'del') res = await client.mod('DELETE', `/messages/${id}`);
    else if (b.dataset.act === 'pin') res = await client.mod('PATCH', '', { pinned_message_id: pinnedId === id ? null : id });
    else {
      if (!window.confirm(t.confirmBan(entry.m.nickname))) return;
      res = await client.mod('POST', '/bans', { message_id: id, delete_messages: false });
    }
    if (!res.ok) showFlash(res.message || t.modFailed);
    else if (b.dataset.act === 'del') remove(id);
  });
  slowSel.addEventListener('change', async () => {
    const res = await client.mod('PATCH', '', { slow_mode_seconds: Number(slowSel.value) });
    if (!res.ok) showFlash(res.message || t.modFailed);
  });
  pauseBtn.addEventListener('click', async () => {
    const res = await client.mod('PATCH', '', { paused: state !== 'paused' });
    if (!res.ok) showFlash(res.message || t.modFailed);
  });

  function setModerator(m: Moderator | null) {
    moderator = m;
    root.classList.toggle('is-mod', !!m);
    modbar.hidden = !m;
    if (m) modName.replaceChildren(h('span', 'jc-badge jc-badge-moderator', t.moderator), h('b', '', m.name));
    showNick();
    setStatus();
  }

  return {
    root,
    reactSlot,
    setModerator,
    flash: showFlash,
    focus: () => input.focus(),
    destroy() {
      off();
      offOffline();
      clearInterval(cooldownTimer);
      clearTimeout(announceTimer);
      root.remove();
    },
  };
}
